import { useState } from 'react';

import MailIcon from '~/assets/icons/Mail';
import TickIcon from '~/assets/icons/Tick';
import WarningIcon from '~/assets/icons/Warning';
import LoginButtons from '~/components/LoginButtons';
import Modals from '~/components/Modals';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import useRegion from '~/hooks/useRegion';
import {
  RegistrationEntryProvider,
  useRegistrationMethod,
  useRegistrationStart
} from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';

const REGISTRATION_ENTRY = { placement: 'gated_card' } as const;

export default function GatedLoginCard() {
  const user = useUser();
  const region = useRegion();

  if (user.isLoggedIn || region.code === 'GB') return null;

  return (
    <RegistrationEntryProvider entry={REGISTRATION_ENTRY}>
      <GatedLoginCardContents />
    </RegistrationEntryProvider>
  );
}

function GatedLoginCardContents() {
  const { isBot } = useRootData();
  const recordRegistrationStart = useRegistrationStart();
  const recordRegistrationMethod = useRegistrationMethod();

  const [loginModalType, setLoginModalType] = useState<
    'login' | 'signUp' | null
  >(null);
  const [socialLoginError, setSocialLoginError] = useState<string | null>(null);

  function onEmailRegisterClick() {
    setLoginModalType('signUp');
    recordRegistrationStart(REGISTRATION_ENTRY);
    recordRegistrationMethod('email');

    new RegisterIntentEvent({
      buttonName: 'SIGN_UP_EMAIL',
      isBot,
      pagePosition: 'Gating Card'
    }).sendToGtm();

    return gtm.trackedButtonClick({
      buttonName: 'SIGN_UP_EMAIL',
      pagePosition: 'Gating Card'
    });
  }

  function onEmailSignInClick() {
    setLoginModalType('login');
    recordRegistrationMethod('existing_account');

    return gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_EMAIL',
      pagePosition: 'Gating Card'
    });
  }

  return (
    <>
      <div className="w-full space-y-5 rounded-lg bg-[#231f20] px-4 py-6 text-white">
        <div className="text-3xl font-light tracking-wide">
          Melden Sie sich an, um zu erfahren, wo Sie kaufen können
        </div>

        <div className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-green-800/30 px-4 py-1 text-sm font-light tracking-wide text-green-500">
          <div>
            <TickIcon fill="#3BBE5B" height="16px" width="16px" />
          </div>
          Einmal anmelden - immer eingeloggt
        </div>

        <p className="w-full text-sm leading-relaxed tracking-wider text-gray-300">
          Vergleichen Sie Lagerbestände und Preise aller Einzelhändler und
          sparen Sie Zeit und Geld.
        </p>

        <div className="space-y-3 text-black">
          <button
            onClick={onEmailRegisterClick}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-green-500 px-4 py-3 text-sm font-bold text-black transition hover:bg-green-600"
          >
            <MailIcon height="18" width="18" stroke="black" />
            Mit E-Mail registrieren
          </button>
          <LoginButtons.Google
            pagePosition="Gating Card"
            onError={setSocialLoginError}
          />
          <LoginButtons.Apple
            pagePosition="Gating Card"
            onError={setSocialLoginError}
          />
          <LoginButtons.Facebook
            pagePosition="Gating Card"
            onError={setSocialLoginError}
          />
          {socialLoginError && <SocialLoginErrorMsg error={socialLoginError} />}
          <button
            onClick={onEmailSignInClick}
            className="w-full rounded-md border border-white px-4 py-1 text-sm font-semibold text-white transition"
          >
            Bereits ein Konto? Anmelden
          </button>
        </div>
      </div>
      {loginModalType &&
        (loginModalType === 'signUp' ? (
          <Modals.SignUp
            directSignUp
            modalType="notifications"
            registrationEntry={REGISTRATION_ENTRY}
            onDismiss={() => setLoginModalType(null)}
          />
        ) : (
          <Modals.Login
            modalType="notifications"
            onDismiss={() => setLoginModalType(null)}
          />
        ))}
    </>
  );
}

function SocialLoginErrorMsg({ error }: { error: string }) {
  return (
    <p className="mt-4 flex items-center gap-2 rounded bg-red-alert/75 p-2 text-sm font-normal text-white">
      <span className="w-6">
        <WarningIcon fill="white" />
      </span>
      {error}
    </p>
  );
}
